import React, {
  useEffect,
  useRef,
  useState,
  useCallback
} from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/mychat.css";


const API = "https://suryojasvi-reflection-api.wyzmindz.com/api";

export default function MyChat() {
  const email = localStorage.getItem("email");
  const navigate = useNavigate();

  // ── existing state (unchanged) ─────────────────────────────
  const [receiverEmail, setReceiverEmail] = useState("");
  const [chatGuid, setChatGuid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [chatList, setChatList] = useState([]);
  const [unread, setUnread] = useState({});
  const [chatPartner, setChatPartner] = useState("");
  const [typingUser, setTypingUser] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [notification, setNotification] = useState(null);

  // ── new: group chat state ──────────────────────────────────
  const [activeChatType, setActiveChatType]       = useState("direct");
  const [showGroupModal, setShowGroupModal]         = useState(false);
  const [groupName, setGroupName]                   = useState("");
  const [groupAvatar, setGroupAvatar]               = useState("👥");
  const [groupMemberInput, setGroupMemberInput]     = useState("");
  const [groupMembers, setGroupMembers]             = useState([]);
  const [memberSuggestions, setMemberSuggestions]   = useState([]);
  const [groupSearchTimer, setGroupSearchTimer]     = useState(null);
  const [currentGroupInfo, setCurrentGroupInfo]     = useState(null);
  const [showGroupInfo, setShowGroupInfo]           = useState(false);
  const [addMemberInput, setAddMemberInput]         = useState("");

  const wsRef       = useRef(null);
  const chatEndRef  = useRef(null);
  const searchTimer = useRef(null);
  const chatGuidRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);


  // ===================== LOAD CHAT LIST =====================
  const loadChatList = useCallback(async () => {
    try {
      const res = await fetch(`${API}/chat/list?viewer_email=${email}`);
      const data = await res.json();
      setChatList(data || []);
    } catch (err) {
      console.log("Chat List Load Failed", err);
    }
  }, [email]);


  // ===================== DELETE CHAT (unchanged) =====================
  const deleteChat = async (e, guid) => {
    e.stopPropagation();
    if (!window.confirm("Delete this chat? This cannot be undone.")) return;
    try {
      await fetch(`${API}/chat/${guid}?viewer_email=${email}`, { method: "DELETE" });
      setChatList(prev => prev.filter(c => c.chat_guid !== guid));
      if (chatGuid === guid) {
        setChatGuid("");
        setMessages([]);
        setChatPartner("");
        setCurrentGroupInfo(null);
        setShowGroupInfo(false);
      }
    } catch (err) {
      alert("Delete failed. Please try again.");
    }
  };

  // ===================== SYNC chatGuidRef (unchanged) =====================
  useEffect(() => { chatGuidRef.current = chatGuid; }, [chatGuid]);


  // ===================== FETCH UNREAD COUNTS (unchanged) =====================
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/chat/unread-counts?viewer_email=${email}`);
      const data = await res.json();
      setUnread(data || {});
    } catch (err) {
      console.log("Unread fetch failed", err);
    }
  }, [email]);


  // ===================== WEBSOCKET (unchanged) =====================
  useEffect(() => {
    if (!email) return;

    const ws = new WebSocket(`wss://suryojasvi-reflection-api.wyzmindz.com/ws/chat?email=${email}`);
    wsRef.current = ws;

    ws.onopen  = () => console.log("WS Connected 👍");
    ws.onclose = () => console.log("WS Closed ❌");

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === "typing") {
        if (data.chat_guid === chatGuidRef.current && data.sender !== email) {
          setTypingUser(data.sender);
          setTimeout(() => setTypingUser(null), 2000);
        }
        return;
      }

      const msgChatGuid = data.chat_guid;
      const isActivChat = msgChatGuid === chatGuidRef.current;

      if (isActivChat) {
        if (data.sender_email === email) return;
        setMessages(prev => [
          ...prev,
          {
            sender_email: data.sender_email,
            sender_name:  data.sender_name || data.sender_email,
            message_text: data.message,
            delivered_at: true,
            seen_at:      true
          }
        ]);
      } else {
        setUnread(prev => ({
          ...prev,
          [msgChatGuid]: (prev[msgChatGuid] || 0) + 1
        }));

        setChatList(currentList => {
          const chat    = currentList.find(c => c.chat_guid === msgChatGuid);
          const name    = chat?.group_name || chat?.participant_name || chat?.participant_email || data.sender_email;
          const preview = (data.message || "").length > 50
            ? data.message.slice(0, 50) + "…"
            : data.message;
          setNotification({ name, message: preview });
          setTimeout(() => setNotification(null), 4000);
          return currentList;
        });
      }

      loadChatList();
    };

    return () => ws.close();
  }, [chatGuid, email, loadChatList]);


  // ===================== START DIRECT CHAT (unchanged) =====================
  const startChat = async () => {
    if (!receiverEmail.trim()) return alert("Enter email to chat");

    const res = await fetch(
      `${API}/chat/direct?sender_email=${email}&receiver_email=${receiverEmail}`,
      { method: "POST" }
    );

    const data = await res.json();
    if (!res.ok) return alert(data.detail || "Chat not allowed");

    openChat(data.chat_guid, "direct");
    loadChatList();
  };


  // ===================== OPEN CHAT (extended for group) =====================
  const openChat = async (guid, type = "direct") => {
    try {
      setChatGuid(guid);
      setActiveChatType(type);
      setShowGroupInfo(false);

      const chat = chatList.find(c => c.chat_guid === guid);
      if (type === "group") {
        setChatPartner(chat?.group_name || "Group");
      } else {
        setChatPartner(chat?.participant_name || chat?.participant_email || "");
      }

      setUnread(prev => ({ ...prev, [guid]: 0 }));

      const res  = await fetch(`${API}/chat/${guid}/messages?viewer_email=${email}`);
      const data = await res.json();

      setMessages(
        data.map(m => ({
          ...m,
          delivered_at: !!m.delivered_at,
          seen_at:      !!m.seen_at
        }))
      );

      await fetch(`${API}/chat/${guid}/seen?viewer_email=${email}`, { method: "PATCH" });

      // Load group info if group chat
      if (type === "group") {
        const infoRes  = await fetch(`${API}/chat/group/${guid}/info?viewer_email=${email}`);
        const infoData = await infoRes.json();
        setCurrentGroupInfo(infoData);
      } else {
        setCurrentGroupInfo(null);
      }

    } catch (err) {
      console.log("Open chat failed", err);
      alert("Failed to fetch — backend not reachable");
    }
  };


  // ===================== SEND MESSAGE (unchanged) =====================
  const sendMessage = async () => {
    if (!text.trim() || !chatGuid) return;

    const msg = text;
    setText("");

    await fetch(
      `${API}/chat/${chatGuid}/send?sender_email=${email}`,
      {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(msg),
      }
    );

    setMessages(prev => [
      ...prev,
      {
        sender_email: email,
        sender_name:  "You",
        message_text: msg,
        delivered_at: true,
        seen_at:      false
      }
    ]);

    loadChatList();
  };


  // ===================== TYPING (unchanged) =====================
  const sendTyping = () => {
    if (!wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN ||
      !chatGuid) return;

    wsRef.current.send(
      JSON.stringify({
        type:      "typing",
        chat_guid: chatGuid,
        sender:    email,
      })
    );
  };


  // ===================== SEARCH (unchanged) =====================
  const handleSearch = (value) => {
    setReceiverEmail(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      if (!value.trim()) return setSuggestions([]);
      const res  = await fetch(`${API}/chat/search?viewer_email=${email}&q=${value}`);
      const data = await res.json();
      setSuggestions(data || []);
    }, 400);
  };

  useEffect(() => {
    loadChatList();
    fetchUnreadCounts();
  }, [loadChatList, fetchUnreadCounts]);


  // ══════════════════════════════════════════════════════════
  // GROUP CHAT — new functions
  // ══════════════════════════════════════════════════════════

  const handleGroupMemberSearch = (value) => {
    setGroupMemberInput(value);
    if (groupSearchTimer) clearTimeout(groupSearchTimer);
    const t = setTimeout(async () => {
      if (!value.trim()) return setMemberSuggestions([]);
      const res  = await fetch(`${API}/chat/search?viewer_email=${email}&q=${value}`);
      const data = await res.json();
      const addedEmails = groupMembers.map(m => m.EmailID);
      setMemberSuggestions((data || []).filter(s => !addedEmails.includes(s.EmailID)));
    }, 400);
    setGroupSearchTimer(t);
  };

  const addMemberToList = (member) => {
    setGroupMembers(prev => [...prev, member]);
    setGroupMemberInput("");
    setMemberSuggestions([]);
  };

  const removeMemberFromList = (emailID) => {
    setGroupMembers(prev => prev.filter(m => m.EmailID !== emailID));
  };

  const createGroup = async () => {
    if (!groupName.trim())         return alert("Enter group name");
    if (groupMembers.length === 0) return alert("Add at least one member");

    const memberEmails = groupMembers.map(m => m.EmailID);
    const params       = new URLSearchParams({
      creator_email: email,
      group_name:    groupName,
      group_avatar:  groupAvatar,
    });

    const res  = await fetch(`${API}/chat/group/create?${params}`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(memberEmails),
    });
    const data = await res.json();

    if (!res.ok) return alert(data.detail || "Group creation failed");

    setShowGroupModal(false);
    setGroupName("");
    setGroupAvatar("👥");
    setGroupMembers([]);
    await loadChatList();
    openChat(data.chat_guid, "group");
  };

  const addMemberFromInfo = async () => {
    if (!addMemberInput.trim() || !currentGroupInfo) return;
    const params = new URLSearchParams({
      requester_email:  email,
      new_member_email: addMemberInput.trim()
    });
    const res  = await fetch(`${API}/chat/group/${currentGroupInfo.chat_guid}/add-member?${params}`, { method: "POST" });
    const data = await res.json();
    alert(data.message || data.detail);
    setAddMemberInput("");
    if (res.ok) {
      const infoRes = await fetch(`${API}/chat/group/${currentGroupInfo.chat_guid}/info?viewer_email=${email}`);
      setCurrentGroupInfo(await infoRes.json());
    }
  };

  const removeMemberFromInfo = async (memberEmail) => {
    const params = new URLSearchParams({ requester_email: email, member_email: memberEmail });
    const res    = await fetch(`${API}/chat/group/${chatGuid}/remove-member?${params}`, { method: "DELETE" });
    const data   = await res.json();
    alert(data.message || data.detail);
    if (res.ok) {
      const infoRes = await fetch(`${API}/chat/group/${chatGuid}/info?viewer_email=${email}`);
      setCurrentGroupInfo(await infoRes.json());
    }
  };

  const leaveGroup = async () => {
    if (!window.confirm("Leave this group?")) return;
    const params = new URLSearchParams({ requester_email: email, member_email: email });
    const res    = await fetch(`${API}/chat/group/${chatGuid}/remove-member?${params}`, { method: "DELETE" });
    const data   = await res.json();
    if (res.ok) {
      setChatList(prev => prev.filter(c => c.chat_guid !== chatGuid));
      setChatGuid(null);
      setMessages([]);
      setChatPartner("");
      setCurrentGroupInfo(null);
      setShowGroupInfo(false);
    } else {
      alert(data.detail || "Failed to leave group");
    }
  };

  const deleteGroup = async () => {
    if (!window.confirm("Delete this group for everyone? This cannot be undone.")) return;
    const res  = await fetch(`${API}/chat/group/${chatGuid}?requester_email=${email}`, { method: "DELETE" });
    const data = await res.json();
    if (res.ok) {
      setChatList(prev => prev.filter(c => c.chat_guid !== chatGuid));
      setChatGuid(null);
      setMessages([]);
      setChatPartner("");
      setCurrentGroupInfo(null);
      setShowGroupInfo(false);
    } else {
      alert(data.detail || "Failed to delete group");
    }
  };

  const viewerUserId  = currentGroupInfo?.participants?.find(p => p.email === email)?.user_id;
  const isGroupAdmin  = currentGroupInfo?.admins?.includes(viewerUserId);


  // ══════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════
  return (
    <div className="chat-popup">

      {/* TOAST NOTIFICATION (unchanged) */}
      {notification && (
        <div className="chat-notification">
          <div className="notification-content">
            <div className="notification-title">💬 {notification.name}</div>
            <div className="notification-message">{notification.message}</div>
          </div>
          <button className="notification-close" onClick={() => setNotification(null)}>✕</button>
        </div>
      )}


      {/* ── LEFT PANEL ──────────────────────────────────────── */}
      <div className="chat-left">
        <h3>Chats</h3>

        {/* Direct chat search (unchanged) */}
        <div className="chat-search" style={{ position: "relative" }}>
          <input
            value={receiverEmail}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Type name or email…"
          />
          <button onClick={startChat} title="Start direct chat">+</button>
        </div>

        {suggestions.length > 0 && (
          <div className="suggestion-box">
            {suggestions.map((s) => (
              <div
                key={s.ID}
                className="suggestion-item"
                onClick={() => { setReceiverEmail(s.EmailID); setSuggestions([]); }}
              >
                <strong>{s.Name}</strong><br />
                <span>{s.EmailID}</span>
              </div>
            ))}
          </div>
        )}

        {/* NEW — Group create button */}
        <button
          className="group-create-btn"
          onClick={() => setShowGroupModal(true)}
        >
          👥 New Group
        </button>

        {/* Chat list (extended to show group name/avatar) */}
        <div className="chat-list">
          {chatList.map((c, i) => (
            <div
              key={i}
              className={`chat-item ${c.chat_guid === chatGuid ? "active" : ""} ${unread[c.chat_guid] > 0 ? "has-unread" : ""}`}
              onClick={() => openChat(c.chat_guid, c.chat_type || "direct")}
            >
              {c.chat_type === "group" ? (
                <div className="chat-item-info">
                  <div className="chat-item-name">
                    <span className="group-badge">{c.group_avatar || "👥"}</span>
                    {c.group_name}
                  </div>
                  {c.last_message_text && (
                    <div className="chat-item-lastmsg">
                      {c.last_message_by ? `${c.last_message_by}: ` : ""}{c.last_message_text}
                    </div>
                  )}
                </div>
              ) : (
                <div className="chat-item-info">
                  <div className="chat-item-name">{c.participant_name || c.participant_email}</div>
                  {c.last_message_text && (
                    <div className="chat-item-lastmsg">{c.last_message_text}</div>
                  )}
                </div>
              )}

              {unread[c.chat_guid] > 0 && (
                <span className="unread-badge">{unread[c.chat_guid]}</span>
              )}

              <button
                className="chat-delete-btn"
                onClick={(e) => deleteChat(e, c.chat_guid)}
                title="Delete chat"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      </div>


      {/* ── RIGHT PANEL ─────────────────────────────────────── */}
      <div className="chat-right">

        <div className="chat-right-header">
          <h2>
            {activeChatType === "group" && "👥 "}
            {chatPartner || "Teams Style Chat"} 💬
          </h2>

          <div className="chat-header-actions">
            {/* Info button — only for group chat */}
            {activeChatType === "group" && chatGuid && (
              <button
                className="group-info-btn"
                onClick={() => setShowGroupInfo(v => !v)}
              >
                ℹ️ Info
              </button>
            )}
            {/* Refresh & Close — always visible */}
            <button className="icon-btn" onClick={() => window.location.reload()}>🔄</button>
            <button className="icon-btn danger" onClick={() => navigate("/dashboard")}>✖</button>
          </div>
        </div>

        {typingUser && <p className="typing">{typingUser} is typing…</p>}

        {/* NEW — Group info panel */}
        {showGroupInfo && currentGroupInfo && (
          <div className="group-info-panel">
            <div className="group-info-header">
              <span className="group-info-avatar">{currentGroupInfo.group_avatar || "👥"}</span>
              <strong>{currentGroupInfo.group_name}</strong>
              <span className="group-member-count">
                {currentGroupInfo.participants?.length} members
              </span>
            </div>

            <div className="group-info-members">
              {currentGroupInfo.participants?.map((p, i) => (
                <div key={i} className="group-member-row">
                  <span>
                    {p.name}
                    {currentGroupInfo.admins?.includes(p.user_id) && (
                      <span className="admin-tag">Admin</span>
                    )}
                  </span>
                  {isGroupAdmin && p.email !== email && (
                    <button
                      className="remove-member-btn"
                      onClick={() => removeMemberFromInfo(p.email)}
                    >✕</button>
                  )}
                </div>
              ))}
            </div>

            {isGroupAdmin && (
              <div className="group-add-member">
                <input
                  placeholder="Add member by email"
                  value={addMemberInput}
                  onChange={e => setAddMemberInput(e.target.value)}
                />
                <button onClick={addMemberFromInfo}>Add</button>
              </div>
            )}

            <div className="group-info-actions">
              <button className="leave-group-btn" onClick={leaveGroup}>🚪 Leave</button>
              {isGroupAdmin && (
                <button className="delete-group-btn" onClick={deleteGroup}>🗑️ Delete Group</button>
              )}
            </div>
          </div>
        )}

        {/* CHAT BOX (extended: show sender name in group) */}
        <div className="chat-box">
          {messages.map((m, i) => (
            <div key={i} className={`msg ${m.sender_email === email ? "you" : "them"}`}>
              {activeChatType === "group" && m.sender_email !== email && (
                <small className="msg-sender-name">
                  {m.sender_name || m.sender_email}
                </small>
              )}
              <small>{m.sender_email === email ? "You" : (activeChatType === "direct" ? m.sender_email : "")}</small>
              <div>{m.message_text}</div>
              {m.sender_email === email && (
                <small>
                  {m.seen_at
                    ? "✔✔ Seen"
                    : m.delivered_at
                      ? "✔ Delivered"
                      : "Sent"}
                </small>
              )}
            </div>
          ))}
          <div ref={chatEndRef}></div>
        </div>

        {/* INPUT (unchanged) */}
        {chatGuid && (
          <div className="chat-input">
            <input
              value={text}
              onChange={(e) => { setText(e.target.value); sendTyping(); }}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder={activeChatType === "group" ? `Message ${chatPartner}…` : "Type message…"}
            />
            <button onClick={sendMessage}>Send</button>
          </div>
        )}
      </div>


      {/* ═══ GROUP CREATE MODAL (NEW) ══════════════════════════ */}
      {showGroupModal && (
        <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="group-modal" onClick={e => e.stopPropagation()}>

            <div className="group-modal-header">
              <h3>👥 Create New Group</h3>
              <button className="modal-close-btn" onClick={() => setShowGroupModal(false)}>✕</button>
            </div>

            {/* Avatar picker */}
            <div className="avatar-picker">
              {["👥", "🚀", "💼", "🎯", "⚡", "🔥", "🌟", "🎉"].map(emoji => (
                <button
                  key={emoji}
                  className={`avatar-option ${groupAvatar === emoji ? "selected" : ""}`}
                  onClick={() => setGroupAvatar(emoji)}
                >{emoji}</button>
              ))}
            </div>

            {/* Group name */}
            <input
              className="group-name-input"
              placeholder="Group name…"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
            />

            {/* Member search */}
            <div style={{ position: "relative" }}>
              <input
                className="group-member-search"
                placeholder="Search & add members…"
                value={groupMemberInput}
                onChange={e => handleGroupMemberSearch(e.target.value)}
              />
              {memberSuggestions.length > 0 && (
                <div className="suggestion-box">
                  {memberSuggestions.map(s => (
                    <div
                      key={s.ID}
                      className="suggestion-item"
                      onClick={() => addMemberToList(s)}
                    >
                      <strong>{s.Name}</strong><br />
                      <span>{s.EmailID}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Added members chips */}
            {groupMembers.length > 0 && (
              <div className="group-members-chips">
                {groupMembers.map(m => (
                  <div key={m.ID} className="member-chip">
                    {m.Name}
                    <button onClick={() => removeMemberFromList(m.EmailID)}>✕</button>
                  </div>
                ))}
              </div>
            )}

            <div className="group-modal-footer">
              <span className="member-count-label">
                {groupMembers.length > 0
                  ? `${groupMembers.length + 1} members (including you)`
                  : "Add members above"}
              </span>
              <button
                className="create-group-btn"
                onClick={createGroup}
                disabled={!groupName.trim() || groupMembers.length === 0}
              >
                Create Group
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
